<script lang="ts" setup>
import { computed } from 'vue'
import type { MemberPageConfig } from '../configs/types'
import novelarcVipIcon from '../icons/novelarc/vip.png'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { resources } from '@/config/brands'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => {
  return templateProducts.value && templateProducts.value.length > 0
})

// 定义样式变量供 CSS v-bind 使用
const avatarBorderColor = props.config.theme.avatarBorder
const memberBgUrl = computed(() => `url(${props.config.resources.memberBg}) no-repeat top center`)
const topUpBtnBg = computed(() => props.config.theme.topUpBtnBg)
const subscribeBg = computed(() => props.config.theme.subscribeBg || 'linear-gradient(90deg, #57483E 0%, #312A24 100%)')
const subscribeText = computed(() => props.config.theme.subscribeText || 'linear-gradient(0deg, #FFC781 0%, #F8B57E 100%)')
const subscribeText2 = computed(() => props.config.theme.subscribeText2 || '#CAA37B')
const vipBtnBg = computed(() => props.config.theme.vipBtnBg || 'linear-gradient(90deg, #FFD8B0 0%, #F9B67D 100%)')
const vipBtnText = computed(() => props.config.theme.vipBtnText || '#270100')
const vipIcon = computed(() => props.config.resources.vipIcon || '/images/novelarc/vip-icon.png')
const memberVipIcon = computed(() => props.config.resources.memberVip || novelarcVipIcon)
const menuIcons = computed(() => ({
  transaction: props.config.menuIcons?.transaction || '/images/novelarc/member/icon-transaction.png',
  unlocked: props.config.menuIcons?.unlocked || '/images/novelarc/member/icon-unlock.png',
  contactUs: props.config.menuIcons?.contactUs || '/images/novelarc/member/icon-contact.png',
  setting: props.config.menuIcons?.setting || '/images/novelarc/member/icon-setting.png',
}))
</script>

<template>
  <div class="page-cover">
    <!-- 头像 -->
    <div class="px-30px pt-40px pb-27px flex items-center justify-between">
      <div class="shrink-0">
        <img v-if="userStore.$state.headImageUrl" class="w-15 h-15 border rounded-full avatar-border" :src="userStore.$state.thirdHeadImgUrl" alt="avatar">
        <img v-else class="w-15 h-15 rounded-full" :src="config.resources.userDefaultAvatar" alt="avatar">
      </div>

      <div class="flex-1 overflow-hidden px-12px">
        <div class="flex items-center width-full">
          <div class="username overflow-hidden whitespace-nowrap text-ellipsis">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </div>
          <div v-if="userStore.$state.subscribe" class="shrink-0">
            <img :src="memberVipIcon" class="h-14px w-auto ml-8px">
          </div>
        </div>
        <p class="uid">
          UID {{ userStore.$state.code }}
        </p>
      </div>
    </div>

    <!-- vip 订阅 -->
    <div v-if="subscribeProducts?.length || userStore.$state.subscribe" class="vip-cover flex items-center justify-between mx-10px px-16px">
      <img :src="vipIcon" class="w-30px h-30px shrink-0" alt="vip icon">
      <div class="flex-1 ml-12px">
        <div class="vip-title">
          <span v-if="!userStore.$state.subscribe">{{ config.texts.getVip }}</span>
          <span v-else>{{ config.texts.vipMembership }}</span>
        </div>
        <div v-if="!userStore.$state.subscribe" class="vip-subtitle">
          {{ config.texts.moreCoinsMoreBenefits }}
        </div>
        <div v-if="userStore.$state.subscribeEndDate" class="vip-subtitle">
          {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
        </div>
      </div>
      <router-link
        v-if="!userStore.$state.subscribe"
        :to="{ name: 'subscribe' }"
      >
        <div class="details-btn flex items-center justify-center" @click="elementClick('Details')">
          {{ config.texts.details }}
        </div>
      </router-link>
    </div>

    <!-- 充值,金币信息 -->
    <div class="wallet-cover mx-10px flex justify-between items-center px-16px">
      <div>
        <div class="text-16px font-medium text-#32353A leading-14px">
          {{ config.texts.myWallet }}
        </div>
        <div class="flex items-center mt-8px">
          <img :src="resources?.coin" width="20px" height="20px" alt="金币图标">
          <strong class="text-#32353A ml-4px">{{ userStore.$state.coinBalance }}</strong>
          <span class="text-12px text-#6A6E7A ml-4px leading-14px">{{ config.texts.coins }}</span>
        </div>
      </div>
      <div v-if="!loading">
        <router-link :to="topupShow ? { name: 'pay' } : { name: 'welfare' }">
          <div class="get-btn flex items-center justify-center" @click="elementClick('TopUp')">
            {{ topupShow ? config.texts.topUp : config.texts.get }}
          </div>
        </router-link>
      </div>
    </div>

    <!-- setting -->
    <div class="mt-10px mx-10px px-20px b-rd-2 bg-n-bg-card">
      <div>
        <router-link
          :to="{ name: 'recharge' }"
          class="w-full h-61px flex items-center justify-between"
        >
          <div class="flex items-center space-x-7px">
            <img :src="menuIcons.transaction" width="20px" height="20px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.transactionHistory }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link
          :to="{ name: 'consume' }"
          class="w-full h-61px flex items-center justify-between border-t border-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <img :src="menuIcons.unlocked" width="20px" height="20px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.chaptersUnlocked }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'contact-us' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('contact_us')">
            <div class="flex items-center space-x-7px">
              <img :src="menuIcons.contactUs" width="20px" height="20px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.contactUs }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'setting' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('setting')">
            <div class="flex items-center space-x-7px">
              <img :src="menuIcons.setting" width="20px" height="20px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.setting }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.page-cover {
  width: 100%;
  min-height: 100vh;
  background: v-bind(memberBgUrl);
  background-size: 100% 100%;
}

.avatar-border {
  border-color: v-bind(avatarBorderColor);
}

.vip-cover {
  width: 355px;
  height: 93px;
  background: v-bind(subscribeBg);
  border-radius: 8px 8px 0 0;
  padding-bottom: 20px;
  box-sizing: border-box;
}

.vip-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 14px;
  background: v-bind(subscribeText);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.vip-subtitle {
  font-weight: 400;
  font-size: 12px;
  color: v-bind(subscribeText2);
  margin-top: 6px;
}

.details-btn {
  width: 80px;
  height: 32px;
  background: v-bind(vipBtnBg);
  border-radius: 16px;
  font-weight: 500;
  font-size: 14px;
  color: v-bind(vipBtnText);
}

.wallet-cover {
  width: 355px;
  height: 77px;
  background: #FFFFFF;
  border-radius: 8px;
  margin-top: -20px;
}

.get-btn {
  width: 80px;
  height: 32px;
  background: v-bind(topUpBtnBg);
  border-radius: 16px;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
}

.username {
  font-weight: 500;
  font-size: 18px;
  color: #32353A;
  line-height: 14px;
}

.uid {
  font-weight: 400;
  font-size: 12px;
  color: #969CAA;
  line-height: 11px;
  margin-top: 8px;
}
</style>
